import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../contexts/FamilyContext';
import { useTimelineTasks } from '../hooks/useTimelineTasks';
import WorkflowTimelineView from '../components/Timeline/WorkflowTimelineView';
import EnhancedSprintBlockV2 from '../components/Timeline/EnhancedSprintBlockV2';
import { supabase } from '../lib/supabase';

/**
 * WorkflowTimelinePage
 *
 * Page wrapper for WorkflowTimelineView that handles data fetching
 */
const WorkflowTimelinePage = () => {
  const { currentUser } = useFamilyContext();
  const [searchParams] = useSearchParams();
  const { timeline: fetchedTimeline, loading, error, refetch } = useTimelineTasks(currentUser?.id);

  // Local state for optimistic updates
  const [timeline, setTimeline] = useState(fetchedTimeline);

  // Get view parameter to determine which view to show
  const viewParam = searchParams.get('view');
  const isByCollegeView = viewParam === 'by-college';
  const isCustomSprintView = viewParam === 'custom-sprint';

  // Extract colleges from timeline for sprint component
  const colleges = React.useMemo(() => {
    if (!timeline) return [];
    const collegeMap = new Map();
    timeline.forEach(item => {
      if (item.college && item.college.id) {
        collegeMap.set(item.college.id, item.college);
      }
    });
    return Array.from(collegeMap.values());
  }, [timeline]);

  // Sync local state with fetched timeline
  useEffect(() => {
    setTimeline(fetchedTimeline);
  }, [fetchedTimeline]);

  // Filter timeline for By College view - only show college-related workflows
  const filteredTimeline = React.useMemo(() => {
    if (!isByCollegeView || !timeline) return timeline;

    // Filter to only include items that have a college association
    return timeline.filter(item => {
      // Check if item has college data
      if (item.college_id || item.college) return true;

      // Check if it's a college-related workflow based on category
      if (item.category === 'application' ||
          item.workflow_metadata?.workflow_type === 'college_application') {
        return true;
      }

      return false;
    });
  }, [timeline, isByCollegeView]);

  const handleTaskUpdate = async (taskId, updates) => {
    try {
      // Optimistic update: Update local state immediately
      setTimeline(prevTimeline =>
        prevTimeline.map(task =>
          task.id === taskId ? { ...task, ...updates } : task
        )
      );

      // Update database in background
      const { error: updateError } = await supabase
        .from('timeline_items')
        .update(updates)
        .eq('id', taskId);

      if (updateError) {
        console.error('Error updating task:', updateError);
        // Revert optimistic update by refetching
        await refetch();
        return { success: false, error: updateError };
      }

      return { success: true };
    } catch (err) {
      console.error('Error updating task:', err);
      // Revert optimistic update
      await refetch();
      return { success: false, error: err };
    }
  };

  /**
   * Bulk complete a subworkflow
   * - Marks mandatory/incomplete tasks as 'completed'
   * - Marks optional/incomplete tasks as 'cancelled' (skipped)
   * - Leaves already completed tasks unchanged
   */
  const handleSubWorkflowComplete = async (subWorkflowKey, workflowKey, steps) => {
    try {
      if (!steps || steps.length === 0) {
        return { success: false, error: 'No steps to update' };
      }

      // Group steps by what action to take
      const toComplete = [];
      const toSkip = [];

      steps.forEach(step => {
        // Skip tasks that are already completed
        if (step.status === 'completed') return;

        // Skip tasks that are already skipped/cancelled
        if (step.status === 'cancelled' || step.status === 'skipped') return;

        const isMandatory = step.workflow_metadata?.importance_level === 'mandatory';

        if (isMandatory) {
          toComplete.push(step.id);
        } else {
          toSkip.push(step.id);
        }
      });

      // Optimistic update: Update local state immediately
      setTimeline(prevTimeline =>
        prevTimeline.map(task => {
          if (toComplete.includes(task.id)) {
            return { ...task, status: 'completed', completed_at: new Date().toISOString() };
          }
          if (toSkip.includes(task.id)) {
            return { ...task, status: 'cancelled' };
          }
          return task;
        })
      );

      // Update database - complete mandatory tasks
      if (toComplete.length > 0) {
        const { error: completeError } = await supabase
          .from('timeline_items')
          .update({
            status: 'completed',
            workflow_phase: 'complete',
            completed_at: new Date().toISOString()
          })
          .in('id', toComplete);

        if (completeError) {
          console.error('Error completing tasks:', completeError);
          await refetch();
          return { success: false, error: completeError };
        }
      }

      // Update database - skip optional tasks
      if (toSkip.length > 0) {
        const { error: skipError } = await supabase
          .from('timeline_items')
          .update({
            status: 'cancelled',
            workflow_phase: 'complete'
          })
          .in('id', toSkip);

        if (skipError) {
          console.error('Error skipping tasks:', skipError);
          await refetch();
          return { success: false, error: skipError };
        }
      }

      return {
        success: true,
        completed: toComplete.length,
        skipped: toSkip.length
      };
    } catch (err) {
      console.error('Error completing subworkflow:', err);
      await refetch();
      return { success: false, error: err };
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: "'Reddit Sans', system-ui, -apple-system, sans-serif",
        color: '#64748b'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f5f5f4',
            borderTop: '4px solid #26a69a',
            borderRadius: '50%',
            margin: '0 auto 1rem',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <div>Loading your workflows...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: "'Reddit Sans', system-ui, -apple-system, sans-serif"
      }}>
        <div style={{
          background: 'white',
          border: '2px solid #fccfcf',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>Error Loading Timeline</h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>{error.message}</p>
          <button
            onClick={refetch}
            style={{
              background: '#26a69a',
              color: 'white',
              border: '2px solid #0f172a',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#fafaf9'
    }}>
      {isCustomSprintView ? (
        /* Custom Sprint View */
        <div style={{ padding: '2rem' }}>
          <EnhancedSprintBlockV2
            timelineItems={timeline || []}
            colleges={colleges}
          />
        </div>
      ) : (
        /* Standard Timeline View */
        <WorkflowTimelineView
          timelineItems={filteredTimeline || []}
          onTaskUpdate={handleTaskUpdate}
          onSubWorkflowComplete={handleSubWorkflowComplete}
          studentId={currentUser?.id}
          currentUser={currentUser}
          groupByCollege={isByCollegeView}
        />
      )}
    </div>
  );
};

export default WorkflowTimelinePage;
